import React, { useState, useEffect } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import "react-datepicker/dist/react-datepicker.css";
import "../../assets/css/GuestMaster.css";
import { getMainGuestsAndAttendees } from "../../utils/getGuestWithAttendees";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { updateOccasionCount, guestAllocation } from "../../server/Api";
import { getAllOccasions } from "../../utils/getAllOccasions"; 
import { fetchAllocations } from "../../utils/getAllocatedGuestAndAttendees";
import axios from "axios";
import * as XLSX from "sheetjs-style";
import { canEditPage, canDeletePage } from "../../utils/pagePermissions";
import {
  parseCapacity,
  countAllocatedForOccasion,
  occasionCapacityMessage,
  capacityApiMessage,
} from "../../utils/capacityLimits";
import CapacityNoticeModal from "../../components/CapacityNoticeModal";
import {
  DEFAULT_QR_THEME,
  getOccasionThemeForId,
} from "../../utils/allocateQrThemes";
import { blobToDataUrl } from "../../utils/eventPassQr";
import {
  generateInvitationCardBlob,
  mapOccasionToInvitationRow,
} from "../../utils/invitationCard";

const QR_MODE_SAME = "same";
const QR_MODE_PER_OCCASION = "per-occasion";

const safeFilePart = (value, fallback = "item") => {
  const cleaned = String(value || "")
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned || fallback;
};

const GuestAllocation = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [guestWithAttendees, setGuestWithAttendees] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const selectedEvent = useSelector((state) => state.auth.selectedEvent);
  const eventEntity = useSelector((state) => state.auth.user);
  const canEdit = canEditPage(selectedEvent, "allocate-guest", eventEntity);
  const canDelete = canDeletePage(selectedEvent, "allocate-guest", eventEntity);
  const [selectedAllocations, setSelectedAllocations] = useState({});
  const [regNoMap, setRegNoMap] = useState({});
  const navigate = useNavigate();
  const [globalSelectAll, setGlobalSelectAll] = useState(false);
  const [qrMode, setQrMode] = useState(() => {
    try {
      const saved = localStorage.getItem(
        `allocate-qr-mode:${selectedEvent?.eventId || "default"}`
      );
      return saved === QR_MODE_PER_OCCASION ? QR_MODE_PER_OCCASION : QR_MODE_SAME;
    } catch {
      return QR_MODE_SAME;
    }
  });
  const [qrPreview, setQrPreview] = useState(null);
  const [qrPreviewItems, setQrPreviewItems] = useState([]);
  const [qrPreviewLoading, setQrPreviewLoading] = useState(false);
  const [pendingQrMode, setPendingQrMode] = useState(null);
  const [showQrSetupModal, setShowQrSetupModal] = useState(false);
  const [qrSetupChoice, setQrSetupChoice] = useState(QR_MODE_SAME);
  const [allocationsLoaded, setAllocationsLoaded] = useState(false);
  const [capacityNotice, setCapacityNotice] = useState(null);

  const getQrPayload = (regNo, occasionId = null) => {
    if (qrMode === QR_MODE_PER_OCCASION && occasionId) {
      return `${regNo}|${occasionId}`;
    }
    return String(regNo);
  };

  const getAllocatedOccasionsForPerson = (personKey) => {
    const allocatedIds = (selectedAllocations[personKey] || []).map(String);
    return occasions.filter((occasion) =>
      allocatedIds.includes(String(occasion._id))
    );
  };

  const getOccasionTheme = (occasionId) =>
    getOccasionThemeForId(occasions, occasionId);

  const generateQRBlob = async (text, personName, occasionList = []) =>
    generateInvitationCardBlob({
      guestName: personName,
      eventName: selectedEvent?.eventName || "our celebration",
      hostedBy: selectedEvent?.hostedBy || "",
      occasions: (occasionList || [])
        .map(mapOccasionToInvitationRow)
        .filter(Boolean),
      qrPayload: text,
    });

  useEffect(() => {
    if (!selectedEvent?.eventId) return;
    try {
      const saved = localStorage.getItem(
        `allocate-qr-mode:${selectedEvent.eventId}`
      );
      setQrMode(
        saved === QR_MODE_PER_OCCASION ? QR_MODE_PER_OCCASION : QR_MODE_SAME
      );
    } catch {
      setQrMode(QR_MODE_SAME);
    }
  }, [selectedEvent?.eventId]);

  const hasAnyAllocation = (allocationMap = {}) =>
    Object.values(allocationMap).some(
      (ids) => Array.isArray(ids) && ids.length > 0
    );

  const markQrSetupDone = (eventId = selectedEvent?.eventId) => {
    try {
      localStorage.setItem(
        `allocate-qr-setup-done:${eventId || "default"}`,
        "1"
      );
    } catch {
      /* ignore */
    }
  };

  const applyQrMode = (mode, { markSetupDone = false } = {}) => {
    setQrMode(mode);
    setPendingQrMode(null);
    setShowQrSetupModal(false);
    setQrPreview(null);
    setQrPreviewItems([]);
    setQrPreviewLoading(false);
    try {
      localStorage.setItem(
        `allocate-qr-mode:${selectedEvent?.eventId || "default"}`,
        mode
      );
      if (markSetupDone) markQrSetupDone();
    } catch {
      /* ignore */
    }
  };

  const requestQrModeChange = (mode) => {
    if (mode === qrMode) return;
    setPendingQrMode(mode);
  };

  const confirmQrSetup = () => {
    applyQrMode(qrSetupChoice || QR_MODE_SAME, { markSetupDone: true });
    toast.success(
      qrSetupChoice === QR_MODE_PER_OCCASION
        ? "Using different QR for each occasion"
        : "Using same QR for all occasions"
    );
  };

  // Fetch occasions
useEffect(() => {
  const fetchOccasions = async () => {
    try {
      if (!selectedEvent?.eventId) return;
      const occasions = await getAllOccasions(selectedEvent.eventId);
      setOccasions(occasions);
    } catch (error) {
      toast.error("Failed to load occasions");
    }
  };

  fetchOccasions();
}, [selectedEvent]);
useEffect(() => {
  const loadAllocations = async () => {
    setAllocationsLoaded(false);
    const { formatted, regNoMap } = await fetchAllocations(selectedEvent?.eventId);
    setSelectedAllocations(formatted);
    setRegNoMap(regNoMap);
    setAllocationsLoaded(true);
  };

  loadAllocations();
}, [selectedEvent]);

  useEffect(() => {
    if (!allocationsLoaded || !selectedEvent?.eventId) return;

    const noAllocations = !hasAnyAllocation(selectedAllocations);
    let setupDone = false;
    try {
      setupDone =
        localStorage.getItem(
          `allocate-qr-setup-done:${selectedEvent.eventId}`
        ) === "1";
    } catch {
      setupDone = false;
    }

    if (noAllocations && !setupDone) {
      setQrSetupChoice(qrMode || QR_MODE_SAME);
      setShowQrSetupModal(true);
    }
  }, [allocationsLoaded, selectedAllocations, selectedEvent?.eventId]);
  const buildPersonQrEntries = async (guestId, attendeeId, name) => {
    const key = `${guestId}-${attendeeId || "null"}`;
    const regNo = regNoMap[key];
    if (!regNo) {
      throw new Error("Reg No not available for this person");
    }

    const allocatedOccasions = getAllocatedOccasionsForPerson(key);
    if (!allocatedOccasions.length) {
      throw new Error("No occasions allocated for this person");
    }

    if (qrMode === QR_MODE_SAME) {
      const payload = getQrPayload(regNo);
      const blob = await generateQRBlob(payload, name, allocatedOccasions);
      return [
        {
          occasionId: "all",
          occasionName: "Invitation",
          payload,
          blob,
          theme: DEFAULT_QR_THEME,
          fileName: `${safeFilePart(name)}-${safeFilePart(regNo)}.png`,
        },
      ];
    }

    const entries = [];
    for (const occasion of allocatedOccasions) {
      const payload = getQrPayload(regNo, occasion._id);
      const blob = await generateQRBlob(payload, name, [occasion]);
      entries.push({
        occasionId: String(occasion._id),
        occasionName: occasion.OccasionName || "Occasion",
        payload,
        blob,
        theme: getOccasionTheme(occasion._id),
        fileName: `${safeFilePart(occasion.OccasionName, "Occasion")}-${safeFilePart(
          regNo
        )}.png`,
      });
    }
    return entries;
  };

  const handleDownloadQR = async (guestId, attendeeId, name) => {
    try {
      const entries = await buildPersonQrEntries(guestId, attendeeId, name);
      if (entries.length === 1) {
        saveAs(entries[0].blob, entries[0].fileName);
        toast.success(`QR downloaded for ${name}`);
        return;
      }

      const zip = new JSZip();
      const folder = zip.folder(safeFilePart(name, "Guest"));
      entries.forEach((entry) => folder.file(entry.fileName, entry.blob));
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `${safeFilePart(name, "Guest")}-QR_Codes.zip`);
      toast.success(`Downloaded ${entries.length} QR codes for ${name}`);
    } catch (err) {
      console.error("QR download failed:", err);
      toast.error(err.message || "Failed to download QR");
    }
  };

  const openQrPreview = async (guestId, attendeeId, name, isAttendee = false) => {
    const key = `${guestId}-${attendeeId || "null"}`;
    const regNo = regNoMap[key];
    if (!regNo) {
      toast.error("Reg No not available for this person");
      return;
    }
    const allocatedOccasions = getAllocatedOccasionsForPerson(key);
    if (!allocatedOccasions.length) {
      toast.error("Allocate at least one occasion first");
      return;
    }

    setQrPreview({
      guestId,
      attendeeId,
      name,
      isAttendee,
      regNo,
      personKey: key,
    });
    setQrPreviewLoading(true);
    setQrPreviewItems([]);

    try {
      const entries = await buildPersonQrEntries(guestId, attendeeId, name);
      const items = await Promise.all(
        entries.map(async (entry) => ({
          ...entry,
          dataUrl: await blobToDataUrl(entry.blob),
        }))
      );
      setQrPreviewItems(items);
    } catch (err) {
      console.error("QR preview failed:", err);
      toast.error(err.message || "Failed to prepare QR preview");
      setQrPreview(null);
    } finally {
      setQrPreviewLoading(false);
    }
  };

  const closeQrPreview = () => {
    setQrPreview(null);
    setQrPreviewItems([]);
    setQrPreviewLoading(false);
  };

  const handleDownloadPreviewItem = (item) => {
    if (!item?.blob) return;
    saveAs(item.blob, item.fileName);
    toast.success(`Downloaded QR for ${item.occasionName}`);
  };

  const handleDownloadAllFromPreview = async () => {
    if (!qrPreview || !qrPreviewItems.length) return;
    try {
      if (qrPreviewItems.length === 1) {
        handleDownloadPreviewItem(qrPreviewItems[0]);
        return;
      }
      const zip = new JSZip();
      const root = zip.folder(
        qrPreview.isAttendee ? "Attendees" : "Guests"
      );
      const personFolder = root.folder(safeFilePart(qrPreview.name, "Guest"));
      qrPreviewItems.forEach((item) => {
        personFolder.file(item.fileName, item.blob);
      });
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(
        content,
        `${safeFilePart(qrPreview.name, "Guest")}-QR_Codes.zip`
      );
      toast.success(
        `Downloaded ${qrPreviewItems.length} QR codes for ${qrPreview.name}`
      );
    } catch (err) {
      console.error("Preview ZIP failed:", err);
      toast.error("Failed to download all QR codes");
    }
  };

  // Fetch guests with attendees
  useEffect(() => {
    const fetchGuests = async () => {
      try {
        if (!selectedEvent?.eventId) return;
        const data = await getMainGuestsAndAttendees(selectedEvent.eventId);
        setGuestWithAttendees(data);
        console.log("Guest & Attendee list fetched successfully:", data);
      } catch (error) {
        console.error("Error fetching guest & attendee list:", error);
        toast.error("Failed to fetch guest & attendee list.");
      }
    };

    fetchGuests();
  }, [selectedEvent]);

  const isOccasionSelected = (personKey, occasionId) =>
    (selectedAllocations[personKey] || [])
      .map((id) => String(id))
      .includes(String(occasionId));

  const getFullName = (guest) => {
    return [guest.prefix, guest.firstName, guest.middleName, guest.lastName]
      .filter(Boolean)
      .join(" ");
  };

  const totalGuests = guestWithAttendees.length;
  const totalAttendees = guestWithAttendees.reduce(
    (sum, entry) => sum + (entry.attendees?.length || 0),
    0
  );

  const TotalGuestCount = totalGuests + totalAttendees;

  const findAllocationOverflow = (nextAllocations, occasionIds = null) => {
    const targets = occasionIds
      ? occasions.filter((occasion) =>
          occasionIds.map(String).includes(String(occasion._id))
        )
      : occasions;
    for (const occasion of targets) {
      const capacity = parseCapacity(occasion.OccasionCapacity);
      if (capacity == null) continue;
      const attempted = countAllocatedForOccasion(
        guestWithAttendees,
        nextAllocations,
        occasion._id
      );
      if (attempted > capacity) {
        return {
          title: "Cannot exceed occasion capacity",
          message: occasionCapacityMessage({
            occasionName: occasion.OccasionName,
            capacity,
            attempted,
          }),
        };
      }
    }
    return null;
  };

  const handleCheckboxChange = (guestId, attendeeId, occasionId, checked) => {
    const key = `${guestId}-${attendeeId || "null"}`;
    const occasionKey = String(occasionId);
    const current = (selectedAllocations[key] || []).map((id) => String(id));
    const updated = checked
      ? [...new Set([...current, occasionKey])]
      : current.filter((id) => id !== occasionKey);
    const next = { ...selectedAllocations, [key]: updated };
    if (checked) {
      const overflow = findAllocationOverflow(next, [occasionKey]);
      if (overflow) {
        setCapacityNotice(overflow);
        return;
      }
    }
    setSelectedAllocations(next);
  };

  const handleSelectAll = (guestId, attendeeId, isChecked, allOccasionIds) => {
    const key = `${guestId}-${attendeeId || "null"}`;
    const nextIds = isChecked ? allOccasionIds.map((id) => String(id)) : [];
    const next = { ...selectedAllocations, [key]: nextIds };
    if (isChecked) {
      const overflow = findAllocationOverflow(next, nextIds);
      if (overflow) {
        setCapacityNotice(overflow);
        return;
      }
    }
    setSelectedAllocations(next);
  };

  const handleGlobalSelectAll = (isChecked) => {
    const newSelectedAllocations = {};
    guestWithAttendees.forEach((entry) => {
      const mainGuest = entry.guest;
      const attendees = entry.attendees || [];
      const allOccasionIds = occasions.map((o) => String(o._id));
      const mainKey = `${mainGuest._id}-null`;
      newSelectedAllocations[mainKey] = isChecked ? [...allOccasionIds] : [];
      attendees.forEach((attendee) => {
        const key = `${mainGuest._id}-${attendee._id}`;
        newSelectedAllocations[key] = isChecked ? [...allOccasionIds] : [];
      });
    });
    if (isChecked) {
      const overflow = findAllocationOverflow(newSelectedAllocations);
      if (overflow) {
        setCapacityNotice(overflow);
        return;
      }
    }
    setGlobalSelectAll(isChecked);
    setSelectedAllocations(newSelectedAllocations);
  };

  // Remove a full guest (including their attendees)
  const handleRemoveGuest = (guestId) => {
    // Remove the guest from the data
    setGuestWithAttendees((prev) =>
      prev.filter((entry) => entry.guest._id !== guestId)
    );

    // Remove related keys from selectedAllocations
    setSelectedAllocations((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        if (key.startsWith(`${guestId}-`)) {
          delete updated[key];
        }
      });
      return updated;
    });
  };

  // Remove a single attendee from a guest
  const handleRemoveAttendee = (guestId, attendeeId) => {
    // Remove the attendee from the data
    setGuestWithAttendees((prev) =>
      prev.map((entry) => {
        if (entry.guest._id === guestId) {
          return {
            ...entry,
            attendees: entry.attendees.filter((a) => a._id !== attendeeId),
          };
        }
        return entry;
      })
    );

    // Remove related key from selectedAllocations
    setSelectedAllocations((prev) => {
      const updated = { ...prev };
      const key = `${guestId}-${attendeeId}`;
      delete updated[key];
      return updated;
    });
  };

  const handleSubmit = async () => {
    const overflow = findAllocationOverflow(selectedAllocations);
    if (overflow) {
      setCapacityNotice(overflow);
      return;
    }

    const payloads = [];

    guestWithAttendees.forEach((entry) => {
      const mainGuest = entry.guest;
      const attendees = entry.attendees || [];
      const mainKey = `${mainGuest._id}-null`;

      payloads.push({
        eventId: selectedEvent?.eventId,
        guestId: mainGuest._id,
        attendeeId: null,
        allocatedOccasionIds: selectedAllocations[mainKey] || [],
        demographic: "Adult",
        gender: mainGuest.gender || "",
        foodPreference: mainGuest.foodPreference || "",
        allergic: mainGuest.allergic || "",
        specialNeeds: mainGuest.specialNeeds || "",
        specialRequest: mainGuest.specialRequest || "",
        enteredBy: eventEntity?.username || "admin",
        modifiedBy: eventEntity?.username || "admin",
      });

      attendees.forEach((attendee) => {
        const key = `${mainGuest._id}-${attendee._id}`;
        payloads.push({
          eventId: selectedEvent?.eventId,
          guestId: mainGuest._id,
          attendeeId: attendee._id,
          allocatedOccasionIds: selectedAllocations[key] || [],
          demographic: attendee.demographic || "",
          gender: attendee.gender || "",
          foodPreference: attendee.foodPreference || "",
          allergic: attendee.allergic || "",
          specialNeeds: attendee.specialNeeds || "",
          specialRequest: attendee.specialRequest || "",
          enteredBy: eventEntity?.username || "admin",
          modifiedBy: eventEntity?.username || "admin",
        });
      });
    });

    try {
      await Promise.all(
        payloads.map((body) => axios.post(guestAllocation(), body))
      );

      // Refresh allocations + reg numbers so QR works immediately (no page refresh)
      const { formatted, regNoMap: nextRegNoMap } = await fetchAllocations(
        selectedEvent?.eventId
      );
      setSelectedAllocations(formatted);
      setRegNoMap(nextRegNoMap);

      // Recount every occasion (including zeros after unselect)
      const occasionAllocationMap = {};
      occasions.forEach((o) => {
        occasionAllocationMap[String(o._id)] = 0;
      });
      Object.values(formatted).forEach((occasionIds) => {
        (occasionIds || []).forEach((occasionId) => {
          const id = String(occasionId);
          occasionAllocationMap[id] = (occasionAllocationMap[id] || 0) + 1;
        });
      });

      await Promise.all(
        Object.entries(occasionAllocationMap).map(([occasionId, count]) =>
          axios.put(updateOccasionCount(occasionId), {
            allocatedCount: count,
          })
        )
      );

      toast.success("Guest allocations saved successfully.");
    } catch (error) {
      console.error("Failed to save allocations or update counts", error);
      if (error.response?.data?.code === "CAPACITY_EXCEEDED") {
        setCapacityNotice({
          title: "Cannot exceed occasion capacity",
          message: capacityApiMessage(error),
        });
      } else {
        toast.error("Failed to save guest allocations or counts.");
      }
    }
  };

  const handleDownloadAllQRCodes = async () => {
    const zip = new JSZip();
    const guestsFolder = zip.folder("Guests");
    const attendeesFolder = zip.folder("Attendees");
    let count = 0;

    for (const entry of guestWithAttendees) {
      const mainGuest = entry.guest;
      const attendees = entry.attendees || [];
      const mainName = getFullName(mainGuest);
      const mainKey = `${mainGuest._id}-null`;

      if (regNoMap[mainKey] && getAllocatedOccasionsForPerson(mainKey).length) {
        try {
          const entries = await buildPersonQrEntries(
            mainGuest._id,
            null,
            mainName
          );
          if (qrMode === QR_MODE_PER_OCCASION) {
            const personFolder = guestsFolder.folder(
              safeFilePart(mainName, "Guest")
            );
            entries.forEach((item) => {
              personFolder.file(item.fileName, item.blob);
              count += 1;
            });
          } else {
            entries.forEach((item) => {
              guestsFolder.file(item.fileName, item.blob);
              count += 1;
            });
          }
        } catch (err) {
          console.error(`Failed to generate QR for guest ${mainName}`, err);
        }
      }

      for (const attendee of attendees) {
        const key = `${mainGuest._id}-${attendee._id}`;
        const attendeeName = getFullName(attendee);
        if (!regNoMap[key] || !getAllocatedOccasionsForPerson(key).length) {
          continue;
        }
        try {
          const entries = await buildPersonQrEntries(
            mainGuest._id,
            attendee._id,
            attendeeName
          );
          if (qrMode === QR_MODE_PER_OCCASION) {
            const personFolder = attendeesFolder.folder(
              safeFilePart(attendeeName, "Attendee")
            );
            entries.forEach((item) => {
              personFolder.file(item.fileName, item.blob);
              count += 1;
            });
          } else {
            entries.forEach((item) => {
              attendeesFolder.file(item.fileName, item.blob);
              count += 1;
            });
          }
        } catch (err) {
          console.error(`Failed to generate QR for attendee ${attendeeName}`, err);
        }
      }
    }

    if (count === 0) {
      toast.error("No valid QR codes found to download.");
      return;
    }

    try {
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, "All_QR_Codes.zip");
      toast.success(`Successfully downloaded ${count} QR codes as zip.`);
    } catch (err) {
      console.error("Failed to create zip:", err);
      toast.error("Failed to download QR codes.");
    }
  };

  const filteredGuestWithAttendees = guestWithAttendees.filter((entry) => {
  const mainName = getFullName(entry.guest).toLowerCase();
  const matchesMain = mainName.includes(searchTerm.toLowerCase());

  const attendeeMatches = (entry.attendees || []).some((attendee) =>
    getFullName(attendee).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return matchesMain || attendeeMatches;
});

const handleExportGuestAllocation = () => {
  if (!guestWithAttendees || guestWithAttendees.length === 0) {
    toast.error("No data available to export.");
    return;
  }

  try {
    const exportRows = [];

    // 1️⃣ Headers row
    const headers = [
      "Record Type",
      "Full Name",
      "Gender",
      "Relationship",
      "Attendee Type",
      "All",
      ...occasions.map((o) => o.OccasionName),
    ];
    exportRows.push(headers);

    // 2️⃣ Totals row (count/capacity)
    const totalGuests = guestWithAttendees.length;
    const totalAttendees = guestWithAttendees.reduce(
      (sum, entry) => sum + (entry.attendees?.length || 0),
      0
    );
    const totalRecords = totalGuests + totalAttendees;

    const occasionCounts = occasions.map((occasion) => {
      let count = 0;
      guestWithAttendees.forEach((entry) => {
        const mainKey = `${entry.guest._id}-null`;
        if (isOccasionSelected(mainKey, occasion._id)) count++;

        (entry.attendees || []).forEach((attendee) => {
          const key = `${entry.guest._id}-${attendee._id}`;
          if (isOccasionSelected(key, occasion._id)) count++;
        });
      });
      return `${count}/${occasion.OccasionCapacity || "-"}`;
    });

    exportRows.push([
      totalRecords,
      `Guests: ${totalGuests}, Attendees: ${totalAttendees}`,
      "", "", "", "", // Gender, Relationship, Attendee Type, All blank
      ...occasionCounts,
    ]);

    // 3️⃣ Data rows
    guestWithAttendees.forEach((entry) => {
      const mainGuest = entry.guest;
      const fullNameMain = getFullName(mainGuest);
      const mainKey = `${mainGuest._id}-null`;
      const mainAllocations = selectedAllocations[mainKey] || [];
      const mainAll =
        mainAllocations.length === occasions.length ? "Y" : ""; // All Y only if all occasions allocated

      exportRows.push([
        "GUEST",
        fullNameMain,
        mainGuest.gender || "",
        "", // Relationship
        "", // Attendee Type
        mainAll,
        ...occasions.map((o) =>
          mainAllocations.map(String).includes(String(o._id)) ? "Y" : "N"
        ),
      ]);

      (entry.attendees || []).forEach((attendee) => {
        const fullNameAtt = getFullName(attendee);
        const key = `${mainGuest._id}-${attendee._id}`;
        const attendeeAllocations = selectedAllocations[key] || [];
        const attendeeAll =
          attendeeAllocations.length === occasions.length ? "Y" : ""; // All Y only if all occasions allocated

        exportRows.push([
          "ATTENDEE",
          fullNameAtt,
          attendee.gender || "",
          attendee.relationWithMainMember || "",
          attendee.demographic || "",
          attendeeAll,
          ...occasions.map((o) =>
            attendeeAllocations.map(String).includes(String(o._id)) ? "Y" : "N"
          ),
        ]);
      });
    });

    const worksheet = XLSX.utils.aoa_to_sheet(exportRows);

// --- Define styles first ---
const headerStyle = {
  font: { bold: true },
  alignment: { horizontal: "center", vertical: "center" },
  fill: { fgColor: { rgb: "C6EFCE" } }, // Light green
};

const totalsStyle = {
  font: { bold: true },
  alignment: { horizontal: "left", vertical: "left" },
  fill: { fgColor: { rgb: "FFEB9C" } }, // Light yellow
};

// --- Apply Header Style (Row 1) ---
headers.forEach((_, i) => {
  const cellRef = XLSX.utils.encode_cell({ r: 0, c: i });
  if (worksheet[cellRef]) worksheet[cellRef].s = headerStyle;
});

// --- Apply Totals Style (Row 2) ---
for (let c = 0; c < headers.length; c++) {
  const cellRef = XLSX.utils.encode_cell({ r: 1, c });
  if (worksheet[cellRef]) worksheet[cellRef].s = totalsStyle;
}


    // Data styling
    for (let r = 1; r < exportRows.length; r++) {
      for (let c = 0; c < headers.length; c++) {
        const cellRef = XLSX.utils.encode_cell({ r, c });
        if (!worksheet[cellRef]) continue;

        worksheet[cellRef].s = worksheet[cellRef].s || {};
        worksheet[cellRef].s.alignment = { horizontal: "left" };

        // Apply green/red for occasions only (skip All column at index 5)
        if (c > 5) {
          const cellValue = worksheet[cellRef].v;
          if (cellValue === "Y") {
            worksheet[cellRef].s.fill = { fgColor: { rgb: "C6EFCE" } }; // Light green
          } else if (cellValue === "N") {
            worksheet[cellRef].s.fill = { fgColor: { rgb: "FFC7CE" } }; // Light red
          }
        }
      }
    }

    // Auto column width
    const columnWidths = headers.map((_, colIndex) => {
      const maxLength = exportRows.reduce((max, row) => {
        const cell = row[colIndex];
        return Math.max(max, cell ? cell.toString().length : 10);
      }, 10);
      return { wch: maxLength + 2 };
    });
    worksheet["!cols"] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Guest Allocation");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "guest_allocation.xlsx";
    link.click();

    toast.success("Guest allocation exported successfully!");
  } catch (error) {
    console.error("Export failed:", error);
    toast.error("Failed to export guest allocation.");
  }
};






  return (
    <div className="container-fluid AllowAttendee">
      <div className="">
        <div className="occasion-top d-flex justify-content-between align-items-center">
          <h2 className="mb-0">Guest Allocation</h2>
          <div style={{ maxWidth: "300px", width: "100%" }}>
            <input
              type="text"
              className="form-control mb-3"
              placeholder="Search Guest or Attendee Name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="border rounded shadow bg-white py-4 px-4">
            <div className="allocation-toolbar mb-3">
              <div
                className="allocation-qr-mode"
                role="group"
                aria-label="QR code mode"
              >
                <div className="allocation-qr-mode-heading">
                  <span className="allocation-qr-mode-label">QR Codes</span>
                  <span className="allocation-qr-mode-hint">
                    Choose how guest passes are generated
                  </span>
                </div>
                <div className="allocation-qr-mode-options">
                  <button
                    type="button"
                    className={`allocation-qr-mode-option is-same${
                      qrMode === QR_MODE_SAME ? " is-active" : ""
                    }`}
                    onClick={() => requestQrModeChange(QR_MODE_SAME)}
                    aria-pressed={qrMode === QR_MODE_SAME}
                  >
                    <span className="allocation-qr-mode-icon" aria-hidden="true">
                      <i className="bi bi-qr-code" />
                    </span>
                    <span className="allocation-qr-mode-copy">
                      <strong>Same QR for all occasions</strong>
                      <em>One pass works across every allocated occasion</em>
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`allocation-qr-mode-option is-per${
                      qrMode === QR_MODE_PER_OCCASION ? " is-active" : ""
                    }`}
                    onClick={() => requestQrModeChange(QR_MODE_PER_OCCASION)}
                    aria-pressed={qrMode === QR_MODE_PER_OCCASION}
                  >
                    <span className="allocation-qr-mode-icon" aria-hidden="true">
                      <i className="bi bi-grid-3x3-gap" />
                    </span>
                    <span className="allocation-qr-mode-copy">
                      <strong>Different QR for each occasion</strong>
                      <em>Separate pass for each allocated occasion</em>
                    </span>
                  </button>
                </div>
              </div>
              <div className="allocation-toolbar-actions d-flex gap-2 flex-wrap">
                {canEdit && (
                  <>
                    <button
                      className="btn btn-outline-secondary btn-sm event-btn"
                      onClick={() => navigate("/guest-master")}
                    >
                      + Add Main Guest
                    </button>

                    <button
                      className="btn btn-outline-secondary btn-sm event-btn"
                      onClick={() => navigate("/guest-attendee")}
                    >
                      + Add Attendee
                    </button>
                  </>
                )}
                {canEdit && (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleDownloadAllQRCodes}
                  >
                    Download All QR Codes (ZIP)
                  </button>
                )}
              </div>
            </div>
            {/* 
            <div className="mb-3">
              <label htmlFor="mainGuest" className="form-label">
                *Main Guest ID/Name
              </label>
              <select
                className="form-select form-select-sm w-auto"
                id="mainGuest"
              >
                <option selected disabled>
                  Select Main Guest ID/Name
                </option>
                <option>Mr. Yogendra Vora</option>
                <option>Mr. Girdhalilal Chhotalal Shah</option>
                <option>Mr. Jigneshbhai Manibhai Doshi</option>
              </select>
            </div> */}
            <div className="mb-3 d-flex justify-content-end">
          
            </div>

            <div className="table-responsive mb-0">
              <table className="table table-bordered align-middle table-hover">
                <thead className="thead-dark table-bg-new">
                  {/* First Header Row */}
                  <tr>
                    <th>Sr No.</th>
                    <th>Guest Name</th>
                    <th className="text-center">Select All</th>
                    {Array.isArray(occasions) &&
                      occasions.map((occasion) => (
                        <th className="text-center" key={occasion._id}>{occasion.OccasionName}</th>
                      ))}
          {canEdit && (

                    <th className="text-center allocation-action-col">Action</th>
          )}
                  </tr>

                  {/* Second Header Row (for counts/descriptions) */}
                  <tr>
                    {/* Sn Count */}
                    <th>
                      <span className="text-sm text-gray-500">
                        ({TotalGuestCount})
                      </span>
                    </th>

                    {/* Guest & Attendee counts */}
                    <th>
                      <span className="text-sm text-gray-500">
                        (Guests: {totalGuests}, Attendees: {totalAttendees})
                      </span>
                    </th>

                    {/* Empty cell under Select All */}
          {/* {canEdit && ( */}

                <th className="text-center">
  <label className="custom-checkbox">
    <input
      type="checkbox"
      checked={globalSelectAll}
      disabled={!canEdit}
      onChange={(e) => handleGlobalSelectAll(e.target.checked)}
    />
    <span className="checkmark"></span>
  </label>
</th>

          {/* )} */}

                    {/* Occasion counts */}
   {Array.isArray(occasions) &&
  occasions.map((occasion) => {
    let count = 0;

    filteredGuestWithAttendees.forEach((entry) => {
      const mainGuestKey = `${entry.guest._id}-null`;
      if (isOccasionSelected(mainGuestKey, occasion._id)) {
        count++;
      }

      (entry.attendees || []).forEach((attendee) => {
        const attendeeKey = `${entry.guest._id}-${attendee._id}`;
        if (isOccasionSelected(attendeeKey, occasion._id)) {
          count++;
        }
      });
    });

    return (
      <th className="text-center" key={`${occasion._id}-count`}>
        <span
          className={`text-sm ${
            parseCapacity(occasion.OccasionCapacity) != null &&
            count > parseCapacity(occasion.OccasionCapacity)
              ? "text-danger"
              : "text-gray-500"
          }`}
        >
          ({count} / {occasion.OccasionCapacity || "—"})
        </span>
      </th>
    );
  })}
  

                    {/* Empty cell under Action */}
          {canEdit && (

                    <th></th>
          )}
                  </tr>
                </thead>

                <tbody>
                  {Array.isArray(guestWithAttendees) &&
                    filteredGuestWithAttendees.map((entry, index) => {
                      const mainGuest = entry.guest;
                      const attendees = entry.attendees || [];
                      const allOccasionIds = occasions.map((o) =>
                        String(o._id)
                      );

                      const isSelectAllMain =
                        selectedAllocations[`${mainGuest._id}-null`] &&
                        selectedAllocations[`${mainGuest._id}-null`].length ===
                          allOccasionIds.length;

                      return (
                        <React.Fragment key={mainGuest?._id || index}>
                          {/* Main Guest Row */}
                          <tr className="table-primary">
                            <td>{index + 1}</td>
                            <td className="text-start">
                              {getFullName(mainGuest)}
                            </td>
                            <td className="text-center">
                              <label className="custom-checkbox">
                                <input
                                  type="checkbox" id="myCheckbox"
                                  checked={isSelectAllMain}
                                  disabled={!canEdit}

                                  onChange={(e) =>
                                    handleSelectAll(
                                      mainGuest._id,
                                      null,
                                      e.target.checked,
                                      allOccasionIds
                                    )
                                  }
                                />
                                <span className="checkmark"></span>
                              </label>
                            </td>
                            {occasions.map((occasion) => {
                              const key = `${mainGuest._id}-null`;
                              const selected = isOccasionSelected(
                                key,
                                occasion._id
                              );

                              return (
                                <td className="text-center" key={occasion._id}>
                                  <label className="custom-checkbox">
                                    <input
                                      type="checkbox"
                                      disabled={!canEdit}

                                      checked={selected}
                                      onChange={(e) =>
                                        handleCheckboxChange(
                                          mainGuest._id,
                                          null,
                                          occasion._id,
                                          e.target.checked
                                        )
                                      }
                                    />
                                    <span className="checkmark"></span>
                                  </label>
                                </td>
                              );
                            })}
          {canEdit && (

                            <td className="text-center allocation-action-cell">
                              <div className="wd200 allocation-action-btns">
                                {canDelete && (
                                  <button
                                    className="btn btn-sm btn-danger remove-btn"
                                    onClick={() =>
                                      handleRemoveGuest(mainGuest._id)
                                    }
                                  >
                                    Remove
                                  </button>
                                )}
                                {selectedAllocations[
                                  `${mainGuest._id}-null`
                                ]?.length > 0 && (
                                  <>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-secondary allocation-preview-btn"
                                      onClick={() =>
                                        openQrPreview(
                                          mainGuest._id,
                                          null,
                                          getFullName(mainGuest),
                                          false
                                        )
                                      }
                                      title="Preview QR"
                                      aria-label={`Preview QR for ${getFullName(
                                        mainGuest
                                      )}`}
                                    >
                                      <i className="bi bi-eye" aria-hidden="true" />
                                    </button>
                                    {qrMode === QR_MODE_SAME && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() =>
                                          handleDownloadQR(
                                            mainGuest._id,
                                            null,
                                            getFullName(mainGuest)
                                          )
                                        }
                                      >
                                        Download QR
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                    )}
                          </tr>

                          {/* Attendee Rows */}
                          {attendees.map((attendee, subIndex) => {
                            const key = `${mainGuest._id}-${attendee._id}`;
                            const isSelectAllAttendee =
                              selectedAllocations[key]?.length ===
                              allOccasionIds.length;

                            return (
                              <tr 
                                key={attendee._id || `attendee-${subIndex}`}
                                className="table-secondary text-sm text-center"
                              >
                                <td className="text-left txt-left">{`${index + 1}.${subIndex + 1}`}</td>
                                <td className="text-start pl-4">
                                  {getFullName(attendee)}{" "}
                                  <span className="text-muted">
                                    (
                                    {attendee.relationWithMainMember ||
                                      "Attendee"}
                                    )
                                  </span>
                                </td>
                                <td>
                                <label className="custom-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={isSelectAllAttendee}
                                    disabled={!canEdit}

                                    onChange={(e) =>
                                      handleSelectAll(
                                        mainGuest._id,
                                        attendee._id,
                                        e.target.checked,
                                        allOccasionIds
                                      )
                                    }
                                  />
                                  <span className="checkmark"></span>
                                </label>
                              </td>
                                {occasions.map((occasion) => {
                                  const selected = isOccasionSelected(
                                    key,
                                    occasion._id
                                  );

                                  return (
                                    <td key={occasion._id}>
                                      <label className="custom-checkbox">
                                        <input
                                          type="checkbox"
                                          disabled={!canEdit}

                                          checked={selected}
                                          onChange={(e) =>
                                            handleCheckboxChange(
                                              mainGuest._id,
                                              attendee._id,
                                              occasion._id,
                                              e.target.checked
                                            )
                                          }
                                        />
                                        <span className="checkmark"></span>
                                        </label>
                                    </td>
                                  );
                                })}
          {canEdit && (

                                <td className="text-center allocation-action-cell">
                                  <div className="wd200 allocation-action-btns">
                                    {canDelete && (
                                      <button
                                        className="btn btn-sm btn-danger remove-btn"
                                        onClick={() =>
                                          handleRemoveAttendee(
                                            mainGuest._id,
                                            attendee._id
                                          )
                                        }
                                      >
                                        Remove
                                      </button>
                                    )}
                                    {selectedAllocations[
                                      `${mainGuest._id}-${attendee._id}`
                                    ]?.length > 0 && (
                                      <>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-secondary allocation-preview-btn"
                                          onClick={() =>
                                            openQrPreview(
                                              mainGuest._id,
                                              attendee._id,
                                              getFullName(attendee),
                                              true
                                            )
                                          }
                                          title="Preview QR"
                                          aria-label={`Preview QR for ${getFullName(
                                            attendee
                                          )}`}
                                        >
                                          <i
                                            className="bi bi-eye"
                                            aria-hidden="true"
                                          />
                                        </button>
                                        {qrMode === QR_MODE_SAME && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() =>
                                              handleDownloadQR(
                                                mainGuest._id,
                                                attendee._id,
                                                getFullName(attendee)
                                              )
                                            }
                                          >
                                            Download QR
                                          </button>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </td>
                          )}
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {canEdit && (
              <div className="page-actions-bar d-flex justify-content-center gap-2">
                <button
                  type="submit"
                  className="btn btn-success btn-save"
                  onClick={handleSubmit}
                >
                  Save
                </button>

                <button
                  type="reset"
                  className="btn excel-btn"
                  onClick={handleExportGuestAllocation}
                >
                  Export to Excel <i className="bi bi-download"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {qrPreview && (
        <div
          className="allocation-qr-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="allocation-qr-modal-title"
          onClick={closeQrPreview}
        >
          <div
            className="allocation-qr-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="allocation-qr-modal-header">
              <div>
                <h5 id="allocation-qr-modal-title" className="mb-0">
                  QR Preview — {qrPreview.name}
                </h5>
                <p className="allocation-qr-modal-sub mb-0">
                  {qrMode === QR_MODE_PER_OCCASION
                    ? "Separate QR for each allocated occasion"
                    : "Same QR for all allocated occasions"}
                  {qrPreview.regNo ? ` · Reg No: ${qrPreview.regNo}` : ""}
                </p>
              </div>
              <div className="allocation-qr-modal-header-actions">
                {qrMode === QR_MODE_PER_OCCASION && (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleDownloadAllFromPreview}
                    disabled={qrPreviewLoading || !qrPreviewItems.length}
                  >
                    Download All QR
                  </button>
                )}
                <button
                  type="button"
                  className="allocation-qr-modal-close"
                  onClick={closeQrPreview}
                  aria-label="Close QR preview"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="allocation-qr-modal-body">
              {qrPreviewLoading ? (
                <div className="allocation-qr-modal-loading">
                  Preparing QR cards…
                </div>
              ) : qrPreviewItems.length ? (
                <div
                  className={`allocation-qr-card-grid${
                    qrPreviewItems.length === 1 ? " is-single" : ""
                  }`}
                >
                  {qrPreviewItems.map((item) => {
                    const theme = item.theme || DEFAULT_QR_THEME;
                    return (
                      <article
                        className="allocation-qr-card"
                        key={item.occasionId}
                        style={{
                          borderColor: theme.bar,
                          background: theme.soft,
                        }}
                      >
                        <div
                          className="allocation-qr-card-label"
                          style={{ color: theme.primary }}
                        >
                          {item.occasionName}
                        </div>
                        <img
                          src={item.dataUrl}
                          alt={`Invitation QR for ${item.occasionName}`}
                          className="allocation-qr-card-image"
                          style={{ borderColor: theme.bar }}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm w-100"
                          onClick={() => handleDownloadPreviewItem(item)}
                        >
                          Download QR
                        </button>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="allocation-qr-modal-loading">
                  No QR codes available.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {pendingQrMode && (
        <div
          className="allocation-qr-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="allocation-qr-mode-confirm-title"
          onClick={() => setPendingQrMode(null)}
        >
          <div
            className="allocation-qr-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h5 id="allocation-qr-mode-confirm-title">Change QR mode?</h5>
            <p>
              Switch to{" "}
              <strong>
                {pendingQrMode === QR_MODE_PER_OCCASION
                  ? "Different QR for each occasion"
                  : "Same QR for all occasions"}
              </strong>
              ? Preview and download actions will follow this setting.
            </p>
            <div className="allocation-qr-confirm-actions">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setPendingQrMode(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() =>
                  applyQrMode(pendingQrMode, { markSetupDone: true })
                }
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {showQrSetupModal && (
        <div
          className="allocation-qr-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="allocation-qr-setup-title"
        >
          <div
            className="allocation-qr-setup-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="allocation-qr-setup-header">
              <h5 id="allocation-qr-setup-title">Choose QR code style</h5>
              <p>
                No guests are allocated yet. Select how QR passes should be
                created for this event before you start allocating.
              </p>
            </div>

            <div className="allocation-qr-setup-options">
              <button
                type="button"
                className={`allocation-qr-setup-option is-same${
                  qrSetupChoice === QR_MODE_SAME ? " is-active" : ""
                }`}
                onClick={() => setQrSetupChoice(QR_MODE_SAME)}
              >
                <span className="allocation-qr-mode-icon" aria-hidden="true">
                  <i className="bi bi-qr-code" />
                </span>
                <span className="allocation-qr-mode-copy">
                  <strong>Same QR for all occasions</strong>
                  <em>One pass works across every allocated occasion</em>
                </span>
              </button>
              <button
                type="button"
                className={`allocation-qr-setup-option is-per${
                  qrSetupChoice === QR_MODE_PER_OCCASION ? " is-active" : ""
                }`}
                onClick={() => setQrSetupChoice(QR_MODE_PER_OCCASION)}
              >
                <span className="allocation-qr-mode-icon" aria-hidden="true">
                  <i className="bi bi-grid-3x3-gap" />
                </span>
                <span className="allocation-qr-mode-copy">
                  <strong>Different QR for each occasion</strong>
                  <em>Separate pass for each allocated occasion</em>
                </span>
              </button>
            </div>

            <div className="allocation-qr-confirm-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmQrSetup}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
      <CapacityNoticeModal
        open={Boolean(capacityNotice)}
        title={capacityNotice?.title}
        message={capacityNotice?.message}
        onClose={() => setCapacityNotice(null)}
      />
    </div>
  );
};

export default GuestAllocation;
